import type { ApiEndpoints } from '@avisoma/shared';

/**
 * Resolves the Categories page's base screen shape from server-derived facts.
 *
 * Ownership boundary:
 * - This module decides which high-level view the page should render
 *   (`loading`, `error`, `ready`, `filtered-empty`, `empty`).
 * - It does NOT decide permissions (`canImportModel`, `canManageCategories`),
 *   action progress (`importLoading`, `isBusy`), or whether a specific
 *   filtered subset should enable page actions. Those stay in the page.
 *
 * Keep this resolver focused on user-visible screen shape. If a future change
 * is really about what the page looks like at a high level, it likely belongs
 * here. If it is really about what the current user may do, or whether a
 * transient action is in progress, it likely belongs in `Categories.tsx`.
 */

export type CategoriesModelHash = ApiEndpoints<string>['/api/system/model-hash']['GET']['response'];

export type CategoriesViewFacts = {
  categoriesStatus: 'loading' | 'error' | 'success';
  categoryCount: number;
  filteredCount: number;
  hasFilters: boolean;
  modelHashStatus: 'loading' | 'error' | 'success';
  modelHash: CategoriesModelHash | null;
};

export type CategoriesViewState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready' }
  | { kind: 'filtered-empty' }
  | { kind: 'empty'; reason: 'uninitialized' | 'generic' };

export function resolveCategoriesViewState(facts: CategoriesViewFacts): CategoriesViewState {
  if (facts.categoriesStatus === 'loading') {
    return { kind: 'loading' };
  }

  if (facts.categoriesStatus === 'error') {
    return { kind: 'error' };
  }

  if (facts.filteredCount > 0) {
    return { kind: 'ready' };
  }

  if (facts.categoryCount > 0 && facts.hasFilters) {
    return { kind: 'filtered-empty' };
  }

  if (facts.categoryCount > 0) {
    return { kind: 'ready' };
  }

  if (facts.modelHashStatus === 'success' && facts.modelHash && isModelHashEmpty(facts.modelHash)) {
    return { kind: 'empty', reason: 'uninitialized' };
  }

  return { kind: 'empty', reason: 'generic' };
}

function isModelHashEmpty(modelHash: CategoriesModelHash) {
  return (
    modelHash.categories === 'empty' &&
    modelHash.categoryClusters === 'empty' &&
    modelHash.rcEmbeddings === 'empty'
  );
}
